<template>
  <div class="flex h-dvh w-full flex-col overflow-hidden bg-white md:bg-ice-50">
    <nuxt-loading-indicator />

    <dev-only>
      <client-only>
        <dev-breakpoint-indicator />
      </client-only>
    </dev-only>

    <!-- The header sits outside the page's scroll region and stays visible at every step. -->
    <header class="relative z-20 h-12 shrink-0 border-b border-charcoal-200 bg-white bg-gradient-to-b from-ice to-white sm:h-16 md:h-[76px]">
      <div class="mx-auto flex h-full w-full max-w-7xl items-center gap-3 px-4 md:px-8">
        <!-- Inside the native app's webview there is nowhere to navigate to, so the logo is just a graphic. -->
        <div v-if="isAppWebView" class="block shrink-0">
          <core-logo class="h-6 lg:h-7" />
        </div>
        <nuxt-link v-else class="block shrink-0 -mt-1" to="/" external>
          <core-logo class="h-6 lg:h-7" />
        </nuxt-link>

        <div class="ml-auto flex items-center gap-3">
          <u-tooltip text="Cart" :popper="{placement: 'bottom'}">
            <nuxt-link
              :to="EXTERNAL_URLS.shoppingCart"
              external
              class="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Cart"
            >
              <u-icon name="i-ri-shopping-cart-2-line" class="text-2xl text-charcoal" />
              <span
                v-if="cartCount"
                class="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red text-white text-2xs leading-4 font-semibold text-center"
              >{{cartCount}}</span>
            </nuxt-link>
          </u-tooltip>
        </div>
      </div>
    </header>

    <main class="relative isolate min-h-0 flex-1">
      <!-- Stationary blue rings are clipped independently of the scrolling page. -->
      <div aria-hidden="true" class="pointer-events-none absolute inset-0 -z-10 hidden overflow-hidden md:block">
        <svg
          v-for="position in ['-top-[320px] -right-[320px]', '-bottom-[320px] -left-[320px]']"
          :key="position"
          :class="position"
          class="absolute h-[640px] w-[640px]"
          viewBox="0 0 640 640"
          fill="none"
          stroke-width="40"
        >
          <circle cx="320" cy="320" r="150" stroke="currentColor" class="text-ice-200/50" />
          <circle cx="320" cy="320" r="230" stroke="currentColor" class="text-sky-100/40" />
          <circle cx="320" cy="320" r="310" stroke="currentColor" class="text-ice-200/30" />
        </svg>
      </div>
      <nuxt-page />
    </main>

    <client-only>
      <u-notifications>
        <template #title="{title}">
          <span>{{ title }}</span>
        </template>
        <template #description="{description}">
          <span>{{ description }}</span>
        </template>
      </u-notifications>
      <core-loading />
      <core-modal-modals />
      <core-offline-screen />
    </client-only>
  </div>
</template>

<script setup>
  // Dedicated framed prints shell: logo and cart for signed-in and anonymous visitors.
  // The native app's webview keeps the logo as a graphic instead of a home link.
  const {isAppWebView} = useWebView();

  const session = useSessionStore();
  const {userStoreInfo} = storeToRefs(session);

  // Same 99+ cap as mars-ui's own cart badge (navigation-primary.vue).
  const cartCount = computed(() => {
    const count = userStoreInfo.value?.cart_count;

    return count > 99 ? '99+' : count;
  });
</script>
